import { useLayoutEffect, useMemo, useState } from "react"
import type { RefObject } from "react"

import { measureTextWidth } from "~/utils/domMeasure"

export type BadgeLike = { id: string | number; name: string }

export type BadgeMetrics = {
  paddingH: number
  maxWidth: number
  gap: number
}

const DEFAULT_BADGE_FONT = "13px ui-sans-serif, system-ui"
const DEFAULT_BADGE_METRICS: BadgeMetrics = { paddingH: 12, maxWidth: 100, gap: 6 }
const DEFAULT_OVERFLOW_SAMPLE_TEXT = ">9"

// Shared across all instances — same stylesheet means identical metrics for every badge.
let _cachedMetrics: BadgeMetrics | null = null

export type UseBadgeOverflowOptions = {
  font?: string
  fallbackMetrics?: BadgeMetrics
  overflowSampleText?: string
  /**
   * When set, skips the internal ResizeObserver and uses this width directly.
   * Useful when the caller already tracks width via another mechanism.
   */
  availableWidth?: number
}

/**
 * How many badges fit on one line in `containerRef`, leaving room for a "+N" badge.
 * Use for responsive chip/tag/pill rows that should collapse when space is tight.
 * Tracks width via ResizeObserver; pass `options.availableWidth` to skip it.
 */
export function useBadgeOverflow<T extends BadgeLike>(
  items: readonly T[],
  containerRef: RefObject<HTMLElement | null>,
  options?: UseBadgeOverflowOptions,
): { visibleCount: number } {
  const font = options?.font ?? DEFAULT_BADGE_FONT
  const fallbackMetrics = options?.fallbackMetrics ?? DEFAULT_BADGE_METRICS
  const overflowSampleText = options?.overflowSampleText ?? DEFAULT_OVERFLOW_SAMPLE_TEXT
  const externalWidth = options?.availableWidth

  const [metrics, setMetrics] = useState<BadgeMetrics>(() => _cachedMetrics ?? fallbackMetrics)
  const [measuredWidth, setMeasuredWidth] = useState(0)

  useLayoutEffect(() => {
    if (_cachedMetrics !== null) return
    const el = containerRef.current
    if (!el) return
    const read = readBadgeMetrics(el)
    if (read) {
      _cachedMetrics = read
      setMetrics(read)
    }
  }, [containerRef, items.length])

  useLayoutEffect(() => {
    if (externalWidth !== undefined) return
    const el = containerRef.current
    if (!el) return
    setMeasuredWidth(el.offsetWidth)
    const observer = new ResizeObserver(() => {
      setMeasuredWidth(el.offsetWidth)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [containerRef, externalWidth])

  const availableWidth = externalWidth ?? measuredWidth

  const visibleCount = useMemo(() => {
    if (items.length === 0) return 0
    if (availableWidth <= 0) return items.length
    return countVisibleBadges(items, availableWidth, metrics, font, overflowSampleText)
  }, [items, availableWidth, metrics, font, overflowSampleText])

  return { visibleCount }
}

function readBadgeMetrics(container: HTMLElement): BadgeMetrics | null {
  const badge = container.firstElementChild?.firstElementChild
    ?.firstElementChild as HTMLElement | null
  if (!badge) return null
  const s = getComputedStyle(badge)
  const paddingH = parseFloat(s.paddingLeft) + parseFloat(s.paddingRight)
  const maxWidth = parseFloat(s.maxWidth)
  const gap = parseFloat(s.marginRight)
  if (paddingH <= 0 || maxWidth <= 0) return null
  return { paddingH, maxWidth, gap }
}

function countVisibleBadges<T extends BadgeLike>(
  items: readonly T[],
  availableWidth: number,
  metrics: BadgeMetrics,
  font: string,
  overflowSampleText: string,
): number {
  const overflowW = renderedOverflowBadgeWidth(metrics, overflowSampleText, font)
  let usedWidth = 0
  let count = 0
  for (let i = 0; i < items.length; i++) {
    const badgeW = renderedBadgeWidth(items[i]!.name, metrics, font)
    const isLast = i === items.length - 1
    const widthNeeded = isLast ? usedWidth + badgeW : usedWidth + badgeW + overflowW
    if (widthNeeded > availableWidth) break
    usedWidth += badgeW
    count++
  }
  return Math.max(1, count)
}

function renderedBadgeWidth(name: string, m: BadgeMetrics, font: string): number {
  const textWidth = Math.ceil(measureTextWidth(name, font))
  return Math.min(textWidth + m.paddingH, m.maxWidth) + m.gap
}

function renderedOverflowBadgeWidth(m: BadgeMetrics, sampleText: string, font: string): number {
  return Math.ceil(measureTextWidth(sampleText, font)) + m.paddingH
}
